import React from "react"
import { Order } from "../models/SortOrder"

/**
 * TODO - remove this function, as well as the negative number/"insert-#" initialization on new rows and instead use frontend specific fields
 */
export const isRowInserted = <Value>(id: Value): id is Exclude<typeof id, number> => {
    if (typeof id === "string") {
        return id?.includes("insert") === true
    }
    if (typeof id === "number") {
        return id < 0
    }
    return false
}

// Handling of inserted id to row to be consistent among all table usages
// uses a negative number for distinction, as sorting between numbers is better than numbers AND strings
export const insertTemporaryTableId = (num: number) => -(num + 1)

export const addTableRow = <R>(row: R, numAddedRows: number, primary: keyof R): R => {
    return {
        ...row,
        [primary]: insertTemporaryTableId(numAddedRows),
    }
}

export interface TableRowOptions<T> {
    canSelectRow?: (row: T) => boolean

    /**
     * This is a bit misleading. It will only disable some of the inputs for a row. If we want to improve this in the
     * future, it should probably include configuration to specify all columns, or specific columns disabled.
     * @param row
     */
    disableRow?: (row: T) => boolean
}

/**
 * Comparison functions for table sort
 */
function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] && a[orderBy] && b[orderBy] < a[orderBy]) {
        return -1
    }
    if (b[orderBy] && a[orderBy] && b[orderBy] > a[orderBy]) {
        return 1
    }
    if (a[orderBy] && !b[orderBy]) {
        return 1
    }
    if (!a[orderBy] && b[orderBy]) {
        return -1
    }
    return 0
}

export function getComparator<T>(order: Order, orderBy: keyof T): (a: T, b: T) => number {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy)
}

export const handleTextfieldPaste = <T extends object>(
    event: React.ClipboardEvent<HTMLDivElement>,
    rowIndex: number,
    columnId: keyof T,
    rows: T[],
): [T[], T[]] => {
    const pasteLines = event.clipboardData.getData("text").split(/\r\n|\r|\n/)

    const startIndex = rowIndex

    // remove unwanted trailing blank line from MS Excel and sometimes txt
    if (pasteLines.slice(-1)[0] === "") {
        pasteLines.pop()
    }

    let allRows: T[] = rows.slice()
    const updatedRows: T[] = []
    // handle multi-line text pasting for the notes column - otherwise let the default action occur
    if (pasteLines.length > 1) {
        event.preventDefault()

        const stopIndex = startIndex + pasteLines.length

        allRows = allRows.map((row, idx) => {
            if (columnId && idx >= startIndex && idx < stopIndex) {
                const val = pasteLines[idx - startIndex]
                row[columnId] = (val !== "" ? val : "") as T[keyof T]
                updatedRows.push(row)
            }
            return row
        })
    }

    return [allRows, updatedRows]
}

export const getSelectedRowsWithShift = <T>(lastSelectedIndex: number, currentSelectedIndex: number, rows: T[]) => {
    if (lastSelectedIndex === -1) {
        return []
    }

    // Order them so indexes in the right order
    const [startIndex, stopIndex] = [lastSelectedIndex, currentSelectedIndex].sort((a, b) => (a > b ? 1 : -1))

    return rows.slice(startIndex, stopIndex + 1)
}
